// P3-014: Battle orchestrator — manages battle lifecycle and coordinates all systems

import type { PrepareResult, ContractResult } from '@/shared/types/phase-contracts';
import type { TilePosition } from '../types/board';
import type { BattleTurnResult } from '../types/battle-internal';
import { useBattleStore } from '../stores/battleStore';
import { useBoardStore } from '../stores/boardStore';
import { EventBus } from '@/shared/game/EventBus';
import * as TurnManager from './TurnManager';
import * as BoardSystem from './BoardSystem';

export type BattleLifecycleState = 'idle' | 'initializing' | 'playing' | 'ended';

let lifecycleState: BattleLifecycleState = 'idle';

/**
 * Get the current lifecycle state.
 */
export function getLifecycleState(): BattleLifecycleState {
  return lifecycleState;
}

/**
 * Initialize a battle with the given prepare and contract data.
 */
export function initBattle(prepare: PrepareResult, contract: ContractResult): void {
  lifecycleState = 'initializing';

  // Initialize battle store
  const battleStore = useBattleStore.getState();
  battleStore.init(prepare, contract);

  // Generate board
  const boardStore = useBoardStore.getState();
  boardStore.generateBoard();

  lifecycleState = 'playing';

  // Notify UI
  EventBus.emit('boardReady');
}

/**
 * Handle a player swap action.
 * This is the main entry point called when the player swaps two tiles.
 * Returns the turn result or null if the swap was invalid.
 */
export function onPlayerSwap(
  pos1: TilePosition,
  pos2: TilePosition,
): BattleTurnResult | null {
  if (lifecycleState !== 'playing') return null;

  // Check if battle is already ended
  const battleStore = useBattleStore.getState();
  if (battleStore.result !== null) return null;

  // Validate the swap first (without committing)
  const { tiles } = useBoardStore.getState();
  if (!BoardSystem.areAdjacent(pos1, pos2)) return null;

  const tile1 = tiles[pos1.x]?.[pos1.y];
  const tile2 = tiles[pos2.x]?.[pos2.y];
  if (!tile1 || !tile2 || tile1.locked || tile2.locked) return null;

  // Execute the full turn
  const turnResult = TurnManager.executeTurn(pos1, pos2);

  if (!turnResult) return null;

  // Emit events for UI updates
  emitTurnEvents(turnResult);

  // Check if battle ended
  if (turnResult.battleOutcome) {
    lifecycleState = 'ended';
    EventBus.emit('battleEnd', { outcome: turnResult.battleOutcome });
  }

  return turnResult;
}

/**
 * Emit EventBus events for a completed turn.
 */
function emitTurnEvents(turnResult: BattleTurnResult): void {
  // Emit cascade steps
  for (let i = 0; i < turnResult.cascadeSteps.length; i++) {
    const step = turnResult.cascadeSteps[i]!;
    EventBus.emit('cascadeStep', {
      step: i + 1,
      matches: step.matches.length,
    });
  }

  // Emit match complete
  EventBus.emit('matchComplete', {
    matches: turnResult.cascadeSteps.reduce((sum, s) => sum + s.matches.length, 0),
    combo: turnResult.cascadeCount,
    damage: turnResult.totalDamage,
  });

  // Emit damage dealt
  const hasAnyCrit = turnResult.matchResolutions.some((r) => r.damage.isCrit);
  EventBus.emit('damageDealt', {
    total: turnResult.totalDamage,
    isCrit: hasAnyCrit,
  });

  // Emit skill triggers
  for (const resolution of turnResult.matchResolutions) {
    if (resolution.skillTriggered) {
      EventBus.emit('skillTriggered', {
        agentId: resolution.skillTriggered.agentId,
        skillName: resolution.skillTriggered.skill.name,
      });
    }
  }

  // Emit overload event
  if (turnResult.overloadEvent?.triggered) {
    EventBus.emit('overloadTriggered', {
      affectedAgentIds: turnResult.overloadEvent.affectedAgentIds,
    });
  }

  // Emit turn end
  const battleStore = useBattleStore.getState();
  const enemy = battleStore.enemy;
  EventBus.emit('turnEnd', {
    turn: turnResult.turnNumber,
    enemyHp: enemy?.hp ?? 0,
    maxHp: enemy?.maxHp ?? 0,
  });
}

/**
 * Reset the orchestrator and all battle systems.
 */
export function resetBattle(): void {
  lifecycleState = 'idle';
  useBattleStore.getState().reset();
  useBoardStore.getState().reset();
}
